import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MyContext } from '../../../context';
import DataTable from '../../../components/tables/DataTable';
import Pagination from '../../../components/Pagination';
import DateRangePicker from '../../../components/DateRangePicker';
import { listWorkflowRuns } from '../../../utils/queries';

const PAGE_SIZE = 20;
const STATUSES = ['PENDING', 'RUNNING', 'WAITING', 'COMPLETED', 'FAILED', 'CANCELLED'];

const formatDuration = (ms) => {
	if (ms == null) return '—';
	const seconds = Math.round(ms / 1000);
	if (seconds < 60) return `${seconds}s`;
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return `${minutes}m`;
	const hours = Math.round(minutes / 60);
	return `${hours}h`;
};

export default function WorkflowRunsList() {
	const { activeStoreId } = useContext(MyContext);
	const [runs, setRuns] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [status, setStatus] = useState('');
	const [range, setRange] = useState({});

	useEffect(() => {
		if (!activeStoreId) return;
		setLoading(true);
		listWorkflowRuns({
			storeId: activeStoreId,
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			status: status || undefined,
			dateFrom: range.from || undefined,
			dateTo: range.to || undefined,
		}).then((result) => {
			if (result?.success) {
				setRuns(result.data.runs);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	}, [page, status, range.from, range.to, activeStoreId]);

	const columns = [
		{
			key: 'workflow_name',
			header: 'Workflow',
			render: (value, row) => (
				<Link to={`/workflows/runs/${row.workflow_instance_id}`} className='text-black font-medium hover:underline'>
					{value || `Workflow #${row.workflow_definition_id}`}
				</Link>
			),
		},
		{
			key: 'entity_id',
			header: 'Entity',
			render: (value, row) => `${row.entity_type} #${value}`,
		},
		{ key: 'status', header: 'Status' },
		{ key: 'current_step_id', header: 'Current Step', render: (value) => value || '—' },
		{ key: 'started_at', header: 'Started', render: (value) => (value ? new Date(value).toLocaleString() : '—') },
		{ key: 'duration_ms', header: 'Duration', render: (value) => formatDuration(value) },
	];

	return (
		<div className='py-8 pr-8'>
			<h1 className='text-2xl font-light tracking-tight mb-6'>Workflow Runs</h1>

			<div className='mb-5 flex items-end gap-4'>
				<div>
					<label>Status</label>
					<select
						className='form-input w-40'
						value={status}
						onChange={(e) => {
							setStatus(e.target.value);
							setPage(0);
						}}
					>
						<option value=''>All statuses</option>
						{STATUSES.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</div>
				<div>
					<label>Started</label>
					<DateRangePicker
						value={range}
						onChange={(item) => {
							setRange(item);
							setPage(0);
						}}
						className='w-64'
						placeholder='All dates'
					/>
				</div>
			</div>

			<DataTable columns={columns} data={runs} loading={loading} emptyHeading='No workflow runs yet' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
